/**
 * Keep the deal placeholder SIDEBAR and the DOCUMENT in step, and keep a
 * placeholder's label out of the user's hands.
 *
 * The sidebar already speaks a `window` CustomEvent protocol from the CKEditor
 * era — it listens for `placeholderDeletedInEditor` (and deletes the
 * `document_placeholder` row), it listens for `placeholderRestoredInEditor` (and
 * rebuilds a row it deleted, when an undo brings the control back), and it
 * dispatches `deletePlaceholderFromSidebar`.
 * The docx-native editor spoke neither, so the two views drifted: a placeholder
 * deleted in the document stayed in the sidebar forever with no way to
 * re-insert it, and one deleted from the sidebar stayed in the document.
 *
 * The third job is the label itself. A placeholder's inner text is a LABEL
 * (`[Tenant]`) or a value written from the sidebar — never something to type
 * over. Left editable, a user could rename `[Tenant]` in the body while the
 * relational row kept the old name, and the two silently disagreed.
 */

import { Plugin, PluginKey } from 'prosemirror-state';
import type { EditorState, Transaction } from 'prosemirror-state';
import type { Node as PMNode } from 'prosemirror-model';

import { PLACEHOLDER_TAG } from '../docxNativeEditorRegistry';

/**
 * Set on a transaction that writes a placeholder's inner content on purpose —
 * the sidebar's value fill. Without it the read-only guard below would reject
 * the very writes the sidebar exists to make.
 */
export const PLACEHOLDER_WRITE_META = 'propdocsPlaceholderWrite';

export const placeholderSyncKey = new PluginKey('propdocsPlaceholderSync');

/**
 * How long to wait before reporting a placeholder as deleted.
 *
 * Deleting the row is irreversible, and Ctrl+Z immediately after a delete is
 * ordinary behaviour — firing on the spot would destroy the row while the user
 * was putting the placeholder back. The uuid is re-checked against the live
 * document when the timer fires and the report is abandoned if it returned.
 */
const DELETE_SETTLE_MS = 1500;

function isPlaceholder(node: PMNode): boolean {
  return node.type.name === 'sdt' && node.attrs.tag === PLACEHOLDER_TAG;
}

interface PlaceholderRef {
  id: string | null;
  name: string | null;
}

/**
 * Every placeholder in the document, keyed by a stable identity.
 *
 * Migrated placeholders carry no uuid in the body SDT — only the alias — so
 * identity falls back to the name. Both are reported so the sidebar can resolve
 * whichever it has.
 */
function placeholderRefs(doc: PMNode): Map<string, PlaceholderRef> {
  const refs = new Map<string, PlaceholderRef>();
  doc.descendants((node) => {
    if (!isPlaceholder(node)) return true;
    const rawId = node.attrs.id;
    const id = rawId != null && String(rawId).length > 0 ? String(rawId) : null;
    const rawName = node.attrs.placeholder ?? node.attrs.alias;
    const name = rawName != null && String(rawName).length > 0 ? String(rawName) : null;
    const key = id ?? (name ? `name:${name.trim().toLowerCase()}` : null);
    if (key) refs.set(key, { id, name });
    return false;
  });
  return refs;
}

/** True when [from, to) intersects the inner content of a placeholder. */
function touchesPlaceholderContent(state: EditorState, from: number, to: number): boolean {
  let hit = false;
  state.doc.nodesBetween(from, to, (node, pos) => {
    if (hit) return false;
    if (!isPlaceholder(node)) return true;
    // The control's inner span. Selecting or replacing the control as a WHOLE
    // is fine — that is a delete, which the sidebar sync handles; only edits
    // that land strictly inside the label are refused.
    // The control's inner span. A change CONFINED to it is an edit of the
    // label, which is refused — including a caret insertion, where from === to.
    // A change that also crosses a boundary is deleting the control itself,
    // which is allowed and reported to the sidebar by the view below.
    const innerFrom = pos + 1;
    const innerTo = pos + node.nodeSize - 1;
    if (from >= innerFrom && to <= innerTo) hit = true;
    return false;
  });
  return hit;
}

export function placeholderSyncPlugin(): Plugin {
  return new Plugin({
    key: placeholderSyncKey,

    filterTransaction(tr: Transaction, state: EditorState) {
      if (!tr.docChanged) return true;
      if (tr.getMeta(PLACEHOLDER_WRITE_META)) return true;
      // Undo/redo replays a write the guard already vetted once.
      if (tr.getMeta('history$')) return true;

      let refuse = false;
      tr.steps.forEach((step) => {
        if (refuse) return;
        const map = step.getMap();
        map.forEach((oldFrom, oldTo) => {
          if (refuse) return;
          if (touchesPlaceholderContent(state, oldFrom, oldTo)) refuse = true;
        });
      });
      return !refuse;
    },

    view() {
      const pending = new Map<string, ReturnType<typeof setTimeout>>();
      // Deletions already reported to the sidebar, which has destroyed their
      // rows. Kept for the life of the editor: undo has no deadline, and the
      // row can only be rebuilt while we still know what was lost.
      const reported = new Map<string, PlaceholderRef>();
      // Undone deletions. Re-announced whenever the sidebar (re)mounts, because
      // only the sidebar can rebuild a row and it may have been closed when the
      // undo happened. Announcing twice is harmless — it keeps no second copy of
      // what was lost, so the repeat is ignored.
      const undone = new Map<string, PlaceholderRef>();

      const announceRestore = (ref: PlaceholderRef): void => {
        window.dispatchEvent(
          new CustomEvent('placeholderRestoredInEditor', {
            detail: { placeholderId: ref.id, placeholderName: ref.name },
          })
        );
      };
      const onSidebarReady = () => {
        for (const ref of undone.values()) announceRestore(ref);
      };
      window.addEventListener('placeholdersSidebarReady', onSidebarReady);

      return {
        update(view, prevState) {
          if (view.state.doc === prevState.doc) return;

          const before = placeholderRefs(prevState.doc);
          const after = placeholderRefs(view.state.doc);

          for (const [key, ref] of before) {
            if (after.has(key) || pending.has(key)) continue;
            pending.set(
              key,
              setTimeout(() => {
                pending.delete(key);
                // Undo may have put it back while the timer ran.
                if (placeholderRefs(view.state.doc).has(key)) return;
                reported.set(key, ref);
                window.dispatchEvent(
                  new CustomEvent('placeholderDeletedInEditor', {
                    detail: { placeholderId: ref.id, placeholderName: ref.name },
                  })
                );
              }, DELETE_SETTLE_MS)
            );
          }

          // A placeholder that came back cancels its own pending report.
          for (const key of after.keys()) {
            const timer = pending.get(key);
            if (timer) {
              clearTimeout(timer);
              pending.delete(key);
            }
            // Back after the report went out — an undo of the deletion. The
            // control is in the document again but its relational row is gone,
            // so the field would be orphaned: no sidebar card, no value, no
            // deal-field link. Ask the sidebar to put the row back.
            const lost = reported.get(key);
            if (lost) {
              reported.delete(key);
              undone.set(key, lost);
              announceRestore(lost);
            }
          }
        },
        destroy() {
          window.removeEventListener('placeholdersSidebarReady', onSidebarReady);
          for (const timer of pending.values()) clearTimeout(timer);
          pending.clear();
          reported.clear();
          undone.clear();
        },
      };
    },
  });
}
